import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { PropTypes } from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import AppHOC from "../../../generics/AppHOC";
import OverLappingSpinner from "../../../common/Spinner/OverLappingSpinner";
import QoEFilter from "./QoEFilter";
import { useMount } from "../../../utils/genericCommon";
import { downloadAsCSVFile } from "../../../utils/common";
import { getTableColumns, setApplicationsData } from "./tableConfig";
import apiService from "../../../config/api-config";
import { useI18n } from "../../../i18n/useI18n";
import ExplainWidget from "../../../common/ExplainWidget";
import { getWidgetSummaryAccess } from "../../../utils/license";
import TableStore from "@harbor/elements/utils/table/store";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import useSelectableRows from "@harbor/elements/utils/table/hooks/selectableRows";
import { getSelectableColumn } from "../../../utils/table";
import { useTableSettings } from "../../../utils/table";
import {
  LOCAL_STORAGE_LABELS,
  APPLICATION_TABLE_DEFAULT_TGL,
} from "../../../utils/enums";
import SettingsDrawer from "../../../common/SettingsDrawer";
import NoData from "../../../common/table/NoData";

const [
  HbrTable,
  HbrPagination,
  HbrInput,
  HbrIcon,
  HbrButton,
  HbrTag,
  HbrSpinner,
] = reactWrapper([
  "hbr-table",
  "hbr-pagination",
  "hbr-input",
  "hbr-icon",
  "hbr-button",
  "hbr-tag",
  "hbr-spinner",
]);
// default sort column and sort order
const defaultSort = [["usage", "desc"]];
// maximum number of selections allowed in the table
const maxRowSelection = 5;
const tableId = "application_dashboard_table_main";

const Table = ({
  globalFilter,
  rowSelect = false,
  setSelectedAppList,
  reporting = false,
  isReportAppDataCallback,
}) => {
  const { tViews } = useI18n();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [, forceUpdate] = useState(null);
  const [exportLoader, setExportLoader] = useState(false);
  const mount = useMount();
  const dataRef = useRef({
    timestamp: 0,
    error: null,
    qoeHealth: "all",
  });
  const widgetSummaryAccess = useMemo(() => getWidgetSummaryAccess(), []);

  useEffect(() => {
    if (!rowSelect) return;
    if (mount.initMount) {
      mount.initMount = false;
      return;
    }
    // reset selections
    setSelectedAppList([]);
    // reload table
    tableStore.setQuery({ page: 0 });
  }, [
    globalFilter.sideBarTimeFilter.current_period[0],
    globalFilter.sideBarTimeFilter.current_period[1],
  ]);

  const openTableSettings = () => {
    tableStore.setState({ settingsOpen: true });
  };
  const selectableRowsHook = useMemo(
    () => useSelectableRows({ selected: [] }),
    [],
  );
  const columnsHook = useColumns({
    config: ({ store }) => {
      // base columns
      let tableColumns;
      if (rowSelect) {
        tableColumns = getTableColumns(navigate, globalFilter.selectedSite);
        tableColumns.unshift(getSelectableColumn(store, setSelectedAppList));
      } else {
        tableColumns = getTableColumns(
          navigate,
          globalFilter.selectedSite,
          openTableSettings,
          reporting,
        );
      }
      return tableColumns;
    },
    order: defaultSort,
  });
  const tableStore = useMemo(() => {
    const store = new TableStore({
      query: {
        page: 0,
        pageLength: reporting ? 100 : 30,
        maxSelections: maxRowSelection,
      },
      hooks: [columnsHook, selectableRowsHook],
      fetchData: async ({ query, setSource, setCount }) => {
        const { page, pageLength, search } = query;
        const timestamp = Date.now();
        let sortAttribute;
        sortAttribute = Object.fromEntries(
          query.order.map(([key, value]) => [
            key.replace(/display_/g, ""),
            value,
          ]),
        );
        if (mount.mounted) setLoader(true);
        const payload = {
          ...(rowSelect
            ? globalFilter.globalV4PayloadSidebar
            : globalFilter.globalV4Payload),
          size: pageLength,
          offset: page * pageLength,
          sort: sortAttribute,
        };

        if (dataRef.current.qoeHealth !== "all") {
          payload.vqoe_status =
            dataRef.current.qoeHealth === "gray"
              ? ["unknown"]
              : [dataRef.current.qoeHealth];
        }
        if (search) payload.search = search;

        Object.assign(dataRef.current, { timestamp, error: null });

        try {
          const res = await apiService.getApplicationTableList(payload);

          if (mount.mounted && timestamp === dataRef.current.timestamp) {
            setLoader(true);

            let tableData = [];
            let selections = [];

            const apiData = res?.data?.data;
            const apiCount = res?.data?.count;
            const apiError = res?.errorObject;

            // error handling
            if (apiError instanceof Object) {
              dataRef.current.error = apiError;
            }

            // validate rows
            if (Array.isArray(apiData)) {
              // 1) Initialize rows + add `id: application`
              tableData = apiData.map((item) => ({
                ...item,
                id: item.application, // FIX: required for getSelectableColumn
              }));

              // 2) Mark first 5 as selected
              selections = tableData
                .slice(0, maxRowSelection)
                .map((item) => item.id);

              tableData = tableData.map((row, index) => ({
                ...row,
                checked: index < maxRowSelection,
              }));

              // 3) Send data to your external handler
              setApplicationsData(tableData);

              // 4) Push selected rows into store
              selectableRowsHook.setSelected(selections);

              // 5) Notify parent
              setSelectedAppList(selections, tableData);
            }

            // update table
            setSource(tableData);
            setCount(apiCount || 0, false);
            isReportAppDataCallback?.(true);
          }
        } catch (err) {
          console.error("fetchData error:", err);
        } finally {
          if (mount.mounted) setLoader(false);
        }
      },
    });
    useTableSettings(
      LOCAL_STORAGE_LABELS.APPLICATION_TABLE,
      APPLICATION_TABLE_DEFAULT_TGL,
      columnsHook,
      store,
    );
    return store;
  }, [
    globalFilter.timeFilter.current_period[0],
    globalFilter.timeFilter.current_period[1],
    globalFilter.currentTimeStamp,
    globalFilter.selectedSite,
  ]);

  const onQoEHealthChange = (event) => {
    dataRef.current.qoeHealth = event.target.value;
    if (tableStore) {
      tableStore.setQuery({ page: 0 }); // refresh table
    }
  };

  useEffect(() => {
    const unsubscribe = tableStore.subscribe(forceUpdate);
    return () => unsubscribe();
  }, [tableStore]);

  const { source, columns, query, count, selected } = tableStore.getState();
  const { search, pageLength, page } = query;

  const onSearch = useCallback(
    (event) => {
      // Support both event types: from input (onHbr-blur, onHbr-clear) and from keydown
      const value = event?.target?.value ?? "";
      // Only trigger search if value changed
      if (value !== search) tableStore.setQuery({ search: value, page: 0 });
    },
    [search, tableStore],
  );

  // update selection on tag list change
  const onTagRemove = (tagName) => {
    // 1) Remove from selectableRowsHook
    const newSelections = selectableRowsHook
      .getSelected()
      .filter((item) => item !== tagName);

    selectableRowsHook.setSelected(newSelections);

    // 3) Inform parent
    setSelectedAppList(newSelections);
  };

  const getExportData = async () => {
    const payload = {
      ...(rowSelect
        ? globalFilter.globalV4PayloadSidebar
        : globalFilter.globalV4Payload),
      sort: Object.fromEntries(
        query.order.map(([key, value]) => [
          key.replace(/display_/g, ""),
          value,
        ]),
      ),
    };

    if (dataRef.current.qoeHealth !== "all") {
      payload.vqoe_status =
        dataRef.current.qoeHealth === "gray"
          ? ["unknown"]
          : [dataRef.current.qoeHealth];
    }
    if (query.search !== "") payload.search = query.search;
    setExportLoader(true);
    const res = await apiService.getApplicationTableList(payload);
    if (mount.mounted === true) {
      if (
        res.errorObject instanceof Object === false &&
        res.data instanceof Object &&
        Array.isArray(res.data.data)
      ) {
        const resData = res.data.data;
        const tableData = setApplicationsData(resData);
        const cols = columns
          .slice(0)
          .filter((col) => col.name !== "Actions" && col.prop !== "actions");
        // exclude checkbox column header
        if (rowSelect === true) cols.shift();
        const columnTitles = cols.map((item) => `"${item.name}"`);
        // get colum header row
        const data = [columnTitles.join(",")];
        // add records
        const columnKeys = cols.map((item) => item.prop);
        for (const item of tableData) {
          const keys = [];
          for (const key of columnKeys) keys.push(item[key]);
          data.push(keys.join(","));
        }
        downloadAsCSVFile(
          data.join("\n"),
          tViews("applicationTableViewApplication"),
        );
      }
      setExportLoader(false);
    }
  };
  const disabled = loader === true;
  return (
    <div
      className="table-container hbr-css__layout-col-md"
      data-cy="applicationTableView"
    >
      {loader === true && <OverLappingSpinner />}
      <div className="hbr-css__layout-col-md">
        <div
          className="hbr-css__text-heading-lg-bold flex-items"
          data-testid="app-table-title"
        >
          {rowSelect === true
            ? tViews("applicationTableTitleCustom")
            : tViews("applicationTableViewApplication")}
          {rowSelect === false && widgetSummaryAccess.available === true && (
            <ExplainWidget
              payload={globalFilter.globalV4Payload}
              widget="applications_table"
            />
          )}
        </div>
        {reporting === false && (
          <>
            <QoEFilter
              value={dataRef.current.qoeHealth}
              onChange={onQoEHealthChange}
              loader={loader}
            />
            <div className="hbr-css__layout-row-xs hbr-css__layout-justify-between">
              <div className="hbr-css__layout-row-xs">
                <HbrInput
                  placeholder={tViews("searchPlaceholder")}
                  clearable
                  onHbr-clear={onSearch}
                  onkeydown={(event) => {
                    if (event.code === "Enter") onSearch(event);
                  }}
                  onHbr-blur={onSearch}
                  disabled={disabled}
                  data-testid="applications-search-input"
                >
                  <HbrIcon
                    slot="prefix"
                    name="magnifying-glass"
                    sentiment="neutral"
                  />
                </HbrInput>
                <div className="hbr-css__filter-description">
                  {`${tViews("applicationTableCount", { resultCount: count })}`}
                </div>
              </div>
              {Number.isFinite(count) && count > 0 && (
                <HbrButton
                  variant="outline"
                  onClick={getExportData}
                  disabled={disabled || exportLoader}
                  data-testid="hbr-export-button"
                >
                  {exportLoader === true ? (
                    <HbrSpinner slot="prefix" />
                  ) : (
                    <HbrIcon slot="prefix" name="upload-simple" />
                  )}
                  {tViews("export")}
                </HbrButton>
              )}
            </div>
          </>
        )}
      </div>
      <HbrTable
        id={tableId}
        data-testid={tableId}
        columns={columns}
        headerSlot={true}
        externalSorting
        className={reporting === true ? "reportApplicationTable" : ""}
        readonly
        resize
        serverSide
        canFocus={false}
        frameSize={source.length}
        useClipboard={false}
        onHbr-before-sort={(event) => {
          const { column, order } = event.detail;
          const columnsHook = tableStore.getHook("columns");
          if (order) columnsHook.sort({ order: [[column.id, order]] });
          else if (column.id === defaultSort[0][0])
            columnsHook.sort({ order: [[column.id, "asc"]] });
          else columnsHook.sort({ order: defaultSort });
        }}
        source={source}
      >
        <NoData count={count} loader={loader} error={dataRef.current.error} />
      </HbrTable>
      <HbrPagination
        className={reporting === true ? "reportPagination" : ""}
        orderArray={[1, 2, 0]}
        tableStore={tableStore}
        pageSize={pageLength}
        itemCount={count}
        showAll
        page={page}
        size="small"
        onHbr-page-changed={(event) => {
          const index = Number.isInteger(event.detail) ? event.detail : 0;
          tableStore.setQuery({ page: index });
        }}
        onHbr-size-changed={(event) => {
          tableStore.setQuery({ pageLength: event.detail.pageSize, page: 0 });
        }}
      />
      {rowSelect === true && (
        <div>
          <hr />
          <div className="hbr-css__layout-row-2xs">
            <div className="hbr-css__filter-description">
              {tViews("applicationTableSelected", {
                resultCount: selected?.length,
              })}
            </div>
            {selectableRowsHook.getSelected().map((item) => (
              <div key={item}>
                <HbrTag removable onHbr-remove={() => onTagRemove(item)}>
                  {item}
                </HbrTag>
              </div>
            ))}
          </div>
          <hr />
        </div>
      )}
      <SettingsDrawer
        tableStore={tableStore}
        localStorageName={LOCAL_STORAGE_LABELS.APPLICATION_TABLE}
        defaultToggles={APPLICATION_TABLE_DEFAULT_TGL}
        dataTestid="application_dashboard_table_main"
      />
    </div>
  );
};

Table.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  rowSelect: PropTypes.bool,
  isReportAppDataCallback: PropTypes.func,
  setSelectedAppList: PropTypes.func.isRequired,
  reporting: PropTypes.bool,
};

export default AppHOC(Table);
